import { useAppStore } from '@/dashboard/store/appStore';
import { usePublicStore } from '@/dashboard/store/publicStore';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, onMounted, onUnmounted } from 'vue';
import { Draft } from '@/dashboard/types/draft';
import { UserRole, UserRoleType } from '@/dashboard/types/user_role';
import { OwnerAdminCreator } from '@/dashboard/constants/roles';

export function useDraftsList() {
  const appStore = useAppStore();
  const publicStore = usePublicStore();

  const { currentProjectUser, currentWorkspaceUser, project, leavedUsers } = storeToRefs(appStore);
  const { projectShortLink, uploadedFilesCount, allFilesCount, drafts, mobileColumns, uploading, uploadingProgress, deletedDrafts, checkedDrafts } =
    storeToRefs(publicStore);

  const draftsFiltered: ComputedRef<Draft[]> = computed(() => drafts.value.filter((item: Draft) => !isDeleted(item)));
  const canUpload = computed(() => {
    const role = project.value?.temporary ? currentWorkspaceUser.value?.role : currentProjectUser.value?.role;

    if (role) {
      return OwnerAdminCreator.includes(role);
    } else {
      return false;
    }
  });

  function isChecked(draft: Draft) {
    return [...checkedDrafts.value].indexOf(draft.id) !== -1;
  }

  function isDeleted(draft: Draft) {
    return deletedDrafts.value.includes(draft.id);
  }

  function addChecked(draft: Draft) {
    if (isChecked(draft)) {
      publicStore.setCheckedDrafts(checkedDrafts.value.filter((item) => item !== draft.id));
    } else {
      publicStore.setCheckedDrafts([...checkedDrafts.value, draft.id]);
    }
  }

  function canDeleteDraft(draft: Draft): boolean {
    const highPermissionRoles: UserRoleType[] = [UserRole.Owner, UserRole.Admin];
    const creatorRoles: UserRoleType[] = [UserRole.Owner, UserRole.Admin, UserRole.Creator];

    const user = currentProjectUser.value;

    if (!user) {
      return false;
    }

    if (highPermissionRoles.includes(user.role)) {
      return true;
    } else if (user.id === draft.owner_id && creatorRoles.includes(user.role)) {
      return true;
    }

    return false;
  }

  function canMoveDraft(draft: Draft): boolean {
    return canDeleteDraft(draft);
  }

  async function deleteDraft(draft: Draft) {
    await publicStore.deleteDrafts([draft]);
  }

  async function moveDraft(draft: Draft, projectId: number) {
    await publicStore.moveDraft(draft.id, projectId);
  }

  async function loadData(data: { loaded: (a: number, b: number) => void }) {
    await publicStore.loadDrafts(data.loaded);
  }

  async function startUpload(files: FileList) {
    let role = currentProjectUser.value?.role;

    if (project.value?.temporary) {
      role = currentWorkspaceUser.value?.role;
    }

    if (role && OwnerAdminCreator.includes(role)) {
      // await publicStore.createDraft(files);
      await publicStore.uploadDrafts(files);
    }
  }

  onMounted(() => {
    publicStore.canModify = true;
  });

  onUnmounted(() => {
    publicStore.canModify = false;
    publicStore.setCheckedDrafts([]);
  });

  return {
    canUpload,
    mobileColumns,
    draftsFiltered,
    uploading,
    uploadingProgress,
    projectShortLink,
    leavedUsers,
    uploadedFilesCount,
    allFilesCount,
    addChecked,
    deleteDraft,
    moveDraft,
    canDeleteDraft,
    canMoveDraft,
    loadData,
    isChecked,
    startUpload,
  };
}
